import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { IconButton, InputAdornment } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';

/**
 * Provides a date range picker component that allows users to select a start and end date.
 * This component is designed to be fully customizable with minimum and maximum date constraints.
 *
 * @param {Object} props - The properties passed to the DateRange component.
 * @param {Object} props.errorMsg - Error messages to display for the date range field.
 * @param {Date} [props.dateEnd=null] - The default end date.
 * @param {Date} [props.dateStart=null] - The default start date.
 * @param {Date} [props.dateMax=null] - The maximum date that can be selected.
 * @param {Date} [props.dateMin=null] - The minimum date that can be selected.
 * @param {string} [props.size='medium'] - The size of the input fields (small, medium).
 * @param {function} props.onChangeDate - Callback function called with the new start and end dates when they are changed.
 * @returns {JSX.Element} The rendered DateRangePicker component.
 */
const MDateRange = (props) => {
  const {
    dateEnd = null,
    dateStart = null,
    dateMax = null,
    dateMin = null,
    errorMsg = '',
    label = '',
    size = 'medium',
    onChangeDate
  } = props;

  const [dates, setDates] = useState([dateStart, dateEnd]);

  const currentDate = new Date();
  const oneYearLater = new Date();
  oneYearLater.setFullYear(currentDate.getFullYear() + 1);

  const minDate = dateMin || currentDate;
  const maxDate = dateMax || oneYearLater;

  // Avoid useEffect to trigger if reference to dates changes but not values
  const stableDates = useMemo(() => [dateStart, dateEnd], [JSON.stringify([dateStart, dateEnd])]);

  let savedDates = {};

  // Update dates when state dates changes (values only)
  useEffect(() => {
    handleChangeRange(stableDates);
  }, [stableDates]);

  const handleChangeRange = ([start, end]) => {
    if (!moment(start).isValid() || !moment(end).isValid()) {
      return;
    }
    savedDates = { start, end: end || dateEnd };
    setDates([savedDates.start, savedDates.end]);
    onChangeDate([savedDates.start, savedDates.end]);
  };

  const Icon = () => {
    if (window.env.isMobileDevice) {
      return;
    }

    return (
      <InputAdornment position="end">
        <IconButton edge="end">
          <CalendarIcon />
        </IconButton>
      </InputAdornment>
    );
  };

  return (
    <DateRangePicker
      label={label}
      localeText={{ start: 'Du', end: 'Au' }}
      minDate={minDate}
      maxDate={maxDate}
      value={dates}
      onChange={handleChangeRange}
      slots={{ field: SingleInputDateRangeField }}
      slotProps={{
        textField: {
          className: 'date-range-picker',
          'data-cy': 'input-date-range-unavailability',
          readOnly: true,
          size,
          error: !!errorMsg,
          helperText: errorMsg,
          InputProps: { endAdornment: <Icon /> }
        }
      }}
      sx={{ width: '100%' }}
      allowSameDateSelection
      fullWidth
      showDaysOutsideCurrentMonth
    />
  );
};

MDateRange.propTypes = {
  dateEnd: PropTypes.object,
  dateStart: PropTypes.object,
  dateMax: PropTypes.object,
  dateMin: PropTypes.object,
  errorMsg: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['medium', 'small']),
  onChangeDate: PropTypes.func
};

export default MDateRange;
